<script lang="ts">
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    getReleases,
    refreshReleases,
    promoteRelease,
    ApiError,
    type ReleasesResponse,
    type ReleaseChannel,
  } from "../lib/api";
  import { session } from "../lib/session.svelte";

  let data: ReleasesResponse | null = null;
  let error: string | null = null;
  let info: string | null = null;
  // Disable buttons for the tag/channel currently being promoted.
  let busy: string | null = null;
  // True while a manual mirror refresh is in flight.
  let refreshing = false;

  async function refresh() {
    try {
      data = await getReleases();
      error = null;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "Could not load releases.";
    }
  }

  async function refreshNow() {
    error = null;
    info = null;
    refreshing = true;
    try {
      data = await refreshReleases();
      info = "Releases refreshed.";
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      error = err instanceof Error ? err.message : "Could not refresh releases.";
    } finally {
      refreshing = false;
    }
  }

  function shortHash(h: string | null | undefined): string {
    return h ? h.slice(0, 7) : "";
  }
  function dateOnly(s: string | null | undefined): string {
    return s ? s.slice(0, 10) : "";
  }
  function lastSynced(s: string | null | undefined): string {
    return s ? new Date(s).toLocaleString() : "—";
  }

  async function promote(channel: ReleaseChannel, tag: string) {
    error = null;
    info = null;
    busy = `${channel}:${tag}`;
    try {
      await promoteRelease(channel, tag);
      info = `Promoted ${channel} → ${tag}`;
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Downgrade guard: confirm + retry with force.
        const current =
          err instanceof ApiError && (err as ApiError & { current?: string }).current
            ? (err as ApiError & { current?: string }).current
            : undefined;
        const ok = confirm(
          `This would downgrade ${channel}${current ? ` (current ${current})` : ""} to ${tag}. Force anyway?`,
        );
        if (!ok) {
          busy = null;
          return;
        }
        try {
          await promoteRelease(channel, tag, true);
          info = `Force-promoted ${channel} → ${tag}`;
          await refresh();
        } catch (err2) {
          error = err2 instanceof Error ? err2.message : "promote failed";
        }
      } else {
        error = err instanceof Error ? err.message : "promote failed";
      }
    } finally {
      busy = null;
    }
  }

  onMount(() => {
    if (!$session.user) {
      push("/");
      return;
    }
    void refresh();
  });

  $: live = data?.pointers.LIVE ?? null;
  $: beta = data?.pointers.BETA ?? null;
  $: tags = data?.tags ?? [];

  function isLive(tag: string): boolean {
    return live?.target_tag === tag;
  }
  function isBeta(tag: string): boolean {
    return beta?.target_tag === tag;
  }
</script>

<section>
  <div class="header">
    <h1>Releases</h1>
    <button
      class="refresh"
      on:click={refreshNow}
      disabled={refreshing}
      data-testid="refresh-releases"
    >
      {refreshing ? "Refreshing…" : "Refresh releases"}
    </button>
  </div>
  <p class="hint" data-testid="releases-hint">
    Releases auto-sync from git every 5 min and are independent of the deployed
    server version. Last synced: {lastSynced(data?.last_synced)}
  </p>
  {#if data?.last_error}
    <p class="warn" data-testid="mirror-error">
      Git mirror sync failing — the list below may be stale. {data.last_error}
    </p>
  {/if}
  {#if error}
    <p class="error" data-testid="error">{error}</p>
  {/if}
  {#if info}
    <p class="info" data-testid="info">{info}</p>
  {/if}

  <div class="pointers" data-testid="pointers">
    <div class="card">
      <h2>LIVE</h2>
      {#if live}
        <p class="tag" data-testid="pointer-live">{live.target_tag}</p>
        <p class="meta">
          {shortHash(live.commit)} · promoted {dateOnly(live.promoted_at)} by
          {live.promoted_by}
        </p>
      {:else}
        <p class="meta" data-testid="pointer-live">— not set —</p>
      {/if}
    </div>
    <div class="card">
      <h2>BETA</h2>
      {#if beta}
        <p class="tag" data-testid="pointer-beta">{beta.target_tag}</p>
        <p class="meta">
          {shortHash(beta.commit)} · promoted {dateOnly(beta.promoted_at)} by
          {beta.promoted_by}
        </p>
      {:else}
        <p class="meta" data-testid="pointer-beta">— not set —</p>
      {/if}
    </div>
  </div>

  <table data-testid="releases-table">
    <thead>
      <tr>
        <th>id</th>
        <th>Version</th>
        <th>Date</th>
        <th>Commit</th>
        <th>Message</th>
        <th>Channel</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each tags as t (t.version)}
        <tr data-testid="release-row-{t.version}">
          <td class="ver">{t.version}</td>
          <td class="ver">{t.version}</td>
          <td>{dateOnly(t.date)}</td>
          <td class="mono">{shortHash(t.commit)}</td>
          <td class="msg">{t.message}</td>
          <td>
            {#if isLive(t.version)}
              <span class="badge live">LIVE</span>
            {/if}
            {#if isBeta(t.version)}
              <span class="badge beta">BETA</span>
            {/if}
          </td>
          <td class="actions">
            <button
              on:click={() => promote("BETA", t.version)}
              disabled={busy !== null || isBeta(t.version)}
              data-testid="promote-beta-{t.version}"
            >
              {busy === `BETA:${t.version}` ? "…" : "Promote → BETA"}
            </button>
            <button
              on:click={() => promote("LIVE", t.version)}
              disabled={busy !== null || isLive(t.version)}
              data-testid="promote-live-{t.version}"
            >
              {busy === `LIVE:${t.version}` ? "…" : "Promote → LIVE"}
            </button>
          </td>
        </tr>
      {/each}
      {#if tags.length === 0}
        <tr><td colspan="7" class="empty">No release tags found.</td></tr>
      {/if}
    </tbody>
  </table>
</section>

<style>
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin: 0 0 0.25rem;
  }
  h1 {
    margin: 0;
  }
  .hint {
    margin: 0 0 1rem;
    color: #9ca3af;
    font-size: 0.8rem;
  }
  h2 {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    color: #34d399;
  }
  .pointers {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    margin-bottom: 1rem;
  }
  .card {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }
  .tag {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: #f3f4f6;
  }
  .meta {
    margin: 0.2rem 0 0;
    color: #9ca3af;
    font-size: 0.8rem;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: #111827;
  }
  th,
  td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
    vertical-align: middle;
  }
  th {
    background: #0f172a;
    color: #9ca3af;
  }
  .ver {
    font-weight: 600;
  }
  .mono {
    font-family: ui-monospace, monospace;
  }
  .msg {
    color: #d1d5db;
    max-width: 28rem;
  }
  .actions {
    display: flex;
    gap: 0.3rem;
    flex-wrap: wrap;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 700;
    margin-right: 0.25rem;
  }
  .badge.live {
    background: #064e3b;
    color: #6ee7b7;
  }
  .badge.beta {
    background: #3b2f06;
    color: #fbbf24;
  }
  .empty {
    color: #6b7280;
    text-align: center;
  }
  .info {
    color: #6ee7b7;
  }
  .error {
    color: #fca5a5;
  }
  .warn {
    margin: 0 0 1rem;
    padding: 0.5rem 0.75rem;
    background: #3b2f06;
    border: 1px solid #78620d;
    border-radius: 4px;
    color: #fbbf24;
    font-size: 0.8rem;
  }
</style>
